import { colors } from '@hazadapt-git/public-core-base'
import {
    Accordion,
    AccordionActions,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography,
} from '@mui/material'
import React, { FC, ReactNode, useState } from 'react'
import { IoChevronDown } from 'react-icons/io5'

interface AnnouncementBannerProps {
    headline: string
    content?: ReactNode
    actions?: ReactNode[]
    backgroundColor?: string
    headlineColor?: string
    headlineTextDecoration?: string
    leftIcon?: ReactNode
    rightIcon?: ReactNode
    expandable?: boolean
    onPress?: () => void
    showBottomDivider?: boolean
    dividerColor?: string
}

export const AnnouncementBanner: FC<AnnouncementBannerProps> = ({
    headline,
    content,
    actions,
    backgroundColor,
    headlineColor,
    headlineTextDecoration,
    leftIcon,
    rightIcon,
    expandable = true,
    onPress,
    showBottomDivider = false,
    dividerColor,
}) => {
    const [expanded, setExpanded] = useState<boolean>(false)

    const handleAccordionChange = () => {
        setExpanded((prev) => !prev)
    }

    if (!expandable) {
        return (
            <Box
                onClick={onPress}
                sx={{
                    backgroundColor,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: { xs: '0.25rem', sm: '0.5rem' },
                    padding: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
                    width: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    cursor: onPress ? 'pointer' : 'default',
                    borderBottom: showBottomDivider
                        ? `1px solid ${dividerColor ?? colors.grays.NIMBUS}`
                        : undefined,
                }}
            >
                {leftIcon && (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', flexShrink: 0 }}
                    >
                        {leftIcon}
                    </Box>
                )}
                <Typography
                    variant="body1"
                    component="p"
                    fontWeight="bold"
                    textAlign="center"
                    noWrap
                    sx={{
                        color: headlineColor,
                        textDecoration: headlineTextDecoration,
                        flex: '0 1 auto',
                        minWidth: 0,
                        maxWidth: '100%',
                        fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '1rem',
                            lg: '1.25rem',
                        },
                        lineHeight: 1.2,
                    }}
                >
                    {headline}
                </Typography>
                {rightIcon && (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', flexShrink: 0 }}
                    >
                        {rightIcon}
                    </Box>
                )}
            </Box>
        )
    }

    return (
        <Accordion
            expanded={expanded}
            onChange={handleAccordionChange}
            disableGutters
            sx={{
                backgroundColor: backgroundColor,
            }}
        >
            <AccordionSummary
                id="announcement-banner-header"
                aria-controls="announcement-banner-content"
                aria-expanded={expanded}
                sx={{
                    px: { xs: 0.5, sm: 1, md: 2 },
                    minHeight: { xs: '2.75rem', sm: '3.25rem' },
                    '&.Mui-expanded': {
                        minHeight: { xs: '2.75rem', sm: '3.25rem' },
                    },
                    '& .MuiAccordionSummary-content': {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minWidth: 0,
                        gap: { xs: '0.25rem', sm: '0.5rem' },
                        my: { xs: 0.5, sm: 1.5 },
                    },
                    '& .icon': {
                        marginLeft: { xs: '0.25rem', sm: '0.5rem' },
                        flexShrink: 0,
                        transition: 'transform 0.3s ease',
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    },
                }}
            >
                {leftIcon && (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', flexShrink: 0 }}
                    >
                        {leftIcon}
                    </Box>
                )}
                <Typography
                    variant="body1"
                    component="p"
                    fontWeight="bold"
                    noWrap
                    sx={{
                        flex: '0 1 auto',
                        minWidth: 0,
                        maxWidth: '100%',
                        fontSize: {
                            xs: '0.75rem',
                            sm: '0.875rem',
                            md: '1rem',
                            lg: '1.25rem',
                        },
                        lineHeight: 1.2,
                    }}
                >
                    {headline}
                </Typography>
                {rightIcon && (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', flexShrink: 0 }}
                    >
                        {rightIcon}
                    </Box>
                )}
                <IoChevronDown
                    size={'1.5rem'}
                    color={colors.grays.NOIR}
                    className="icon"
                />
            </AccordionSummary>
            <AccordionDetails id="announcement-banner-content">
                {content}
            </AccordionDetails>
            <AccordionActions
                sx={{
                    paddingBottom: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
                    paddingRight: { xs: '0.5rem', sm: '0.75rem', md: '1rem' },
                }}
            >
                {actions}
            </AccordionActions>
        </Accordion>
    )
}
